import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch } from "Redux/store";
import {
  checkNotificationsCount,
  checkUrgentNotifications,
  deleteAllNotifications,
  getNotifications,
  markAllAsRead,
} from "Redux/Notifications/reducer";
import { NOTIFICATIONS_LIMIT } from "Shared/Constants/app";
import NotificationsList from "./NotificationsList";
import { useSelector } from "react-redux";
import {
  selectNotificationsState,
  showNotificationView,
  NotificationView,
} from "Redux/Notifications/slice";
import Drawer from "Components/UI/Drawer/Drawer";
import styles from "./Notification.module.scss";
import {
  CheckMarkIcon as DoneAllRounded,
  TrashIcon as DeleteOutlineRounded,
} from "Assets";

type Props = {
  /** null before the first open. */
  isOpen: boolean | null;
  onClose: () => void;
};

/** The notification inbox, in the same side drawer as the create forms. */
export const Notification = ({ isOpen, onClose }: Props) => {
  const dispatch = useAppDispatch();
  const state = useSelector(selectNotificationsState);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const latest = useRef({ state, isOpen, unreadOnly });
  latest.current = { state, isOpen, unreadOnly };
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const refreshCounts = useCallback(() => {
    const current = latest.current.state;
    const now = Date.now();
    if (
      !current.priorityLoading &&
      (!current.priorityFetchedAt || now - current.priorityFetchedAt >= 30000)
    ) {
      dispatch(
        checkUrgentNotifications(
          current.priorityFetchedAt ? { forceRefresh: true } : null,
        ),
      );
    }
    if (
      !current.countLoading &&
      (!current.countFetchedAt || now - current.countFetchedAt >= 30000)
    ) {
      dispatch(
        checkNotificationsCount(
          current.countFetchedAt ? { forceRefresh: true } : null,
        ),
      );
    }
  }, [dispatch]);

  const refreshList = useCallback(
    (view: NotificationView) => {
      const current = latest.current.state;
      const cached =
        current.views[view] ||
        (view === "unread" && current.views.all && !current.views.all.data.next
          ? current.views.all
          : undefined);
      dispatch(showNotificationView(view));
      if (cached && Date.now() - cached.loadedAt < 30000) return;
      if (current.loading && current.activeView === view) return;
      dispatch(
        getNotifications({
          offset: 0,
          limit: Math.max(
            NOTIFICATIONS_LIMIT,
            cached?.data.results.length || 0,
          ),
          ...(view === "unread" ? { is_read: false } : {}),
          ...(cached ? { forceRefresh: true } : {}),
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    refreshCounts();
    const refresh = () => {
      if (document.visibilityState === "hidden") return;
      const current = latest.current;
      if (current.state.actionLoading) return;
      refreshCounts();
      if (current.isOpen) refreshList(current.unreadOnly ? "unread" : "all");
    };
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [refreshCounts, refreshList, state.cacheScope]);

  useEffect(() => {
    if (!isOpen) {
      setConfirmDelete(false);
      return;
    }
    refreshCounts();
    refreshList(unreadOnly ? "unread" : "all");
  }, [isOpen, unreadOnly, refreshCounts, refreshList, state.cacheScope]);

  const runAction = async (action: "read" | "delete") => {
    if (state.actionLoading) return;
    setActionError(null);
    try {
      await dispatch(
        action === "read" ? markAllAsRead() : deleteAllNotifications(),
      ).unwrap();
      setConfirmDelete(false);
    } catch {
      setActionError(
        action === "read"
          ? "Could not mark notifications as read. Please try again."
          : "Could not delete notifications. Please try again.",
      );
    }
  };

  const hasItems = state.notifications.results.length > 0;

  return (
    <Drawer
      open={Boolean(isOpen)}
      onClose={onClose}
      id="notifications-panel"
      title="Notifications"
      subtitle="Reminders and updates for your fleet."
      aria-label="Notifications"
      closeLabel="Close notifications"
      width={480}
      footer={
        hasItems ? (
          <div className={styles.panelFooter}>
            <span>Updates automatically</span>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={state.actionLoading}
            >
              <DeleteOutlineRounded aria-hidden="true" />
              Delete all
            </button>
          </div>
        ) : undefined
      }
    >
      <div className={styles.inbox}>
        <div className={styles.toolbar}>
          <div
            role="group"
            className={styles.filters}
            aria-label="Filter notifications"
          >
            <button
              type="button"
              aria-pressed={!unreadOnly}
              onClick={() => setUnreadOnly(false)}
              disabled={state.actionLoading}
            >
              All activity
            </button>
            <button
              type="button"
              aria-pressed={unreadOnly}
              onClick={() => setUnreadOnly(true)}
              disabled={state.actionLoading}
            >
              Unread{" "}
              <span className={styles.count}>{state.notificationsCount}</span>
            </button>
          </div>
          <button
            className={styles.markRead}
            type="button"
            onClick={() => runAction("read")}
            disabled={state.actionLoading || !state.notificationsCount}
            title="Mark all as read"
          >
            <DoneAllRounded aria-hidden="true" />
            <span>Mark all as read</span>
          </button>
        </div>
        {confirmDelete && (
          <div className={styles.confirmDelete}>
            <p>Delete all notifications from your inbox?</p>
            <div className={styles.actions}>
              <button
                type="button"
                onClick={() => runAction("delete")}
                disabled={state.actionLoading}
              >
                Confirm delete
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                disabled={state.actionLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {state.actionLoading && (
          <p role="status" className={styles.refreshStatus}>
            Updating notifications…
          </p>
        )}
        {actionError && (
          <p role="alert" className={styles.error}>
            {actionError}
          </p>
        )}
        <NotificationsList onClose={onClose} unreadOnly={unreadOnly} />
      </div>
    </Drawer>
  );
};
